import React, { useState, forwardRef } from 'react';

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  technologies: string[];
  demo: string;
  github: string;
}

interface AnimateCard {
  y: number;
  opacity: number;
}

const Portfolio: React.ForwardRefRenderFunction<HTMLElement> = (props, ref) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [animateCard, setAnimateCard] = useState<AnimateCard>({ y: 0, opacity: 1 });

  const projects: Project[] = [
    {
      id: 1,
      title: 'Money Management System',
      description: 'A full-featured money management system in ASP.NET Core MVC.',
      image: 'https://images.unsplash.com/photo-1523289333742-be1143f6b766?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      category: 'web',
      technologies: [ 'ASP.NET MVC', 'SQL Server', 'Azure'],
      demo: 'https://incassoportaal.nl',
      github: '#'
    },
    {
      id: 2,
      title: 'Project Management Tool',
      description: 'A full-featured web application with React.js frontend and ASP.NET Core backend',
      image: 'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      category: 'web',
      technologies: ['React.js', 'ASP.NET Web API', 'SQL Server'],
      demo: 'https://radvix.io',
      github: '#'
    },
    {
      id: 3,
      title: 'Crypto Tradding wallet',
      description: 'A fully functioning web application using React.js frontend and and ASP.NET Core backend',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      category: 'web',
      technologies: ['React.js', 'ASP.NET Web API', 'SQL Server'],
      demo: 'https://planetnine.com',
      github: '#'
    },
    {
      id: 4,
      title: 'License Management System',
      description: 'A fully functioning web application using Next.js frontend and and ASP.NET Core backend',
      image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      category: 'desktop',
      technologies: ['Next.js', 'ASP.NET Web API', 'SQL Server'],
      demo: '#',
      github: '#'
    },
    {
      id: 5,
      title: 'Social Media Dashboard',
      description: 'A platform where users can get recommendations for local talents and services based on their preferences and location.',
      image: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      category: 'web',
      technologies: ['React.js', 'Chart.js', 'Node js', 'MongoDb'],
      demo: '#',
      github: '#'
    },
    {
      id: 6,
      title: 'Hospital Management System',
      description: 'A hospital management system desktop application to manage patient data and appointments.',
      image: 'https://img.freepik.com/free-photo/stomatolog-nurse-tooth-clinic-checking-patient-appointment-looking-computer-monitor-stomatology-assistant-teeth-doctor-discussing-reception-dental-office_482257-11962.jpg?semt=ais_hybrid&w=740&q=80',
      category: 'desktop',
      technologies: ['ASP.NET WPF', 'SQL Server'],
      demo: '#',
      github: '#'
    }
  ];

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);

    setAnimateCard({ y: 100, opacity: 0 });
    setTimeout(() => {
      setAnimateCard({ y: 0, opacity: 1 });
    }, 500);
  };

  const filteredProjects = activeFilter === 'all' ?
    projects :
    projects.filter((project) => project.category === activeFilter);

  return (
    <section id="portfolio" className="section-padding" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 section-reveal" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-white md:text-4xl font-bold section-title text-center">
            My Portfolio
          </h2>
          <p className="text-white mt-4 max-w-3xl mx-auto">
            Here's a selection of projects I've worked on. Each project represents different skills and technologies in my toolkit.
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-4 mb-12 section-reveal" style={{ animationDelay: '0.4s' }}>
          <button
            onClick={() => handleFilterClick('all')}
            className={`filter-btn px-6 py-2 rounded-full border hover:border-primary ${activeFilter === 'all' ? 'active' : 'bg-white text-gray-800'}`}
          >
            All
          </button>
          <button
            onClick={() => handleFilterClick('web')}
            className={`filter-btn px-6 py-2 rounded-full border hover:border-primary ${activeFilter === 'web' ? 'active' : 'bg-white text-gray-800'}`}
          >
            Web Applications
          </button>
          <button
            onClick={() => handleFilterClick('desktop')}
            className={`filter-btn px-6 py-2 rounded-full border hover:border-primary ${activeFilter === 'desktop' ? 'active' : 'bg-white text-gray-800'}`}
          >
            Desktop Applications
          </button>
          {/* <button
            onClick={() => handleFilterClick('mobile')}
            className={`filter-btn px-6 py-2 rounded-full border hover:border-primary ${activeFilter === 'mobile' ? 'active' : 'bg-white text-gray-800'}`}
          >
            Mobile Applications
          </button> */}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id} 
              className="portfolio-card bg-white rounded-lg overflow-hidden shadow-md section-reveal"
              style={{ animationDelay: `${0.6 + index * 0.2}s` }}
            >
              <div className="relative overflow-hidden h-60">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="portfolio-overlay absolute inset-0 flex flex-col justify-end p-6">
                  <div className="flex space-x-3">
                    <a
                      href={project.demo}
                      className="bg-white text-primary p-2 rounded-full hover:bg-primary hover:text-white transition-all"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-eye"></i>
                    </a>
                    <a
                      href={project.github}
                      className="bg-white text-primary p-2 rounded-full hover:bg-primary hover:text-white transition-all"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-github"></i>
                    </a>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                <p className="text-gray-600 mb-4">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-primary px-3 py-1 rounded-full text-sm"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                {/* <div className="flex justify-between">
                  <a
                    href={project.demo}
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Live Demo
                  </a>
                  <a
                    href={project.github}
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Source Code
                  </a>
                </div> */}
              </div>
            </div>
          ))}
        </div>
        
        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <h3 className="text-xl text-gray-600">No projects in this category yet.</h3>
          </div>
        )}
      </div>
    </section>
  );
};

export default forwardRef(Portfolio);